/**
 * FeatureFlagClient — framework-agnostic core.
 *
 * Works in webapp (Vite), espotz-mobile-app (React Native), and espotz-backend
 * (NestJS / Node 18+). One ~150-line file you copy into each app and then
 * wrap with platform-specific UI (React hook, NestJS service, etc.).
 *
 * Design rules:
 *   1. `isEnabled()` is SYNCHRONOUS — never blocks rendering. If the CDN fetch
 *      hasn't returned yet, fall back to bundled defaults.
 *   2. Flags default to `false` when missing. Code must always opt in.
 *   3. CDN failure is silent — keep last known good values. Default-false
 *      on a totally cold start. Never let a flag service outage brick the app.
 *   4. Local dev story is layered: overrides > CDN > bundled defaults > false.
 *
 * Precedence (highest wins):
 *   - Runtime override (setOverride / window.__ff.set / setFlag in React)
 *   - Server-resolved flags (sensitive tier, pushed via setServerFlags —
 *     the backend evaluates region rules and hands us booleans only)
 *   - CDN-fetched flags (live, refreshed every refreshIntervalMs)
 *   - Bundled defaults passed to the constructor
 *   - `false`
 *
 * Sensitive-tier keys (e.g. buyECCrypto) are deliberately absent from the
 *  CDN file and bundled defaults, so before the server push they resolve
 *  `false` — fail closed, no flash of region-blocked UI.
 */

export type Flags = Readonly<Record<string, boolean>>;

export interface FeatureFlagClientOptions {
  /**
   * Full URL of the env-specific JSON, e.g. 'https://ff.espotz.live/prod/env.json'.
   * Omit to disable remote fetching entirely — useful in local dev where
   * env.json on disk is the source of truth and the CDN should not be
   * consulted at all. With no URL, the client returns values purely from
   * defaults + overrides.
   */
  url?: string;

  /**
   * Last-known-good defaults compiled into the app. Used when CDN is
   * unreachable on cold start, or for keys missing from the CDN response.
   * Copy the contents of flags/dev.json or flags/prod.json from this repo
   * into each app at build time (see README for setup).
   */
  defaults: Flags;

  /**
   * Programmatic overrides (e.g. from env vars, a local feature-flags.local.json,
   * or runtime DevTools tweaks). Highest precedence. Pass `undefined` or `{}`
   * in production.
   */
  overrides?: Flags;

  /** Default: 60_000 (60s). Set to 0 to disable polling. */
  refreshIntervalMs?: number;

  /** Called on every fetch error. Default: console.warn. */
  onError?: (err: Error) => void;

  /**
   * Inject a custom fetch (e.g. node-fetch shim, RN polyfill). Defaults to
   * the global fetch — works in browser, RN, and Node 18+.
   */
  fetchFn?: typeof fetch;
}

type Listener = () => void;

export class FeatureFlagClient {
  private readonly url: string | undefined;
  private readonly defaults: Flags;
  private readonly refreshIntervalMs: number;
  private readonly onError: (err: Error) => void;
  private readonly fetchFn: typeof fetch;

  private remote: Flags = {};
  private serverFlags: Flags | null = null;
  private overrides: Record<string, boolean> = {};
  private remoteEverSucceeded = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private listeners = new Set<Listener>();
  private initPromise: Promise<void> | null = null;

  constructor(opts: FeatureFlagClientOptions) {
    this.url = opts.url;
    this.defaults = opts.defaults;
    this.refreshIntervalMs = opts.refreshIntervalMs ?? 60_000;
    this.onError =
      opts.onError ?? (err => console.warn('[feature-flags]', err.message));
    // Bind fetch to globalThis. The browser's `fetch` is a Window method and
    // throws "Illegal invocation" when called as a method on any other object
    // (since we'd be calling it as `this.fetchFn(...)`). globalThis works in
    // browser, Node 18+, and React Native — all the environments we target.
    this.fetchFn = opts.fetchFn ?? fetch.bind(globalThis);
    this.overrides = { ...(opts.overrides ?? {}) };
  }

  /**
   * Kick off the initial fetch and start background polling. Idempotent —
   * safe to call multiple times. Does NOT throw on fetch failure; just logs
   * and falls back to defaults.
   *
   * Await this at app boot if you want flags ready before the first render,
   * but it's safe NOT to await — `isEnabled()` works immediately using
   * defaults and overrides.
   */
  init(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    // No URL → no remote fetching. Local dev mode where env.json is the
    // source of truth. defaults + overrides still drive isEnabled().
    if (!this.url) {
      this.initPromise = Promise.resolve();
      return this.initPromise;
    }
    this.initPromise = this.refresh();
    if (this.refreshIntervalMs > 0 && this.timer === null) {
      this.timer = setInterval(() => {
        this.refresh().catch(() => {
          /* refresh already logs via onError */
        });
      }, this.refreshIntervalMs);
    }
    return this.initPromise;
  }

  /** Manually trigger a refresh. Errors are caught and reported via onError.
   *  No-op when the client was constructed without a URL (local dev mode). */
  async refresh(): Promise<void> {
    if (!this.url) return;
    try {
      // Cache-bust on the *client* side; CloudFront still respects its own
      // 60s TTL. This just prevents intermediate proxies / SW from serving
      // truly stale data on a forced refresh.
      const res = await this.fetchFn(this.url, { cache: 'no-cache' });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status} fetching ${this.url}`);
      }
      const json = (await res.json()) as unknown;
      this.remote = sanitize(json);
      this.remoteEverSucceeded = true;
      this.notify();
    } catch (err) {
      this.onError(err instanceof Error ? err : new Error(String(err)));
    }
  }

  /**
   * Synchronously check a flag. Returns `false` if unknown OR if no
   * source has provided a value yet. Hot path — keep this cheap.
   */
  isEnabled(name: string): boolean {
    if (Object.prototype.hasOwnProperty.call(this.overrides, name)) {
      return this.overrides[name];
    }
    if (this.serverFlags !== null && name in this.serverFlags) {
      return this.serverFlags[name];
    }
    if (this.remoteEverSucceeded && name in this.remote) {
      return this.remote[name];
    }
    if (name in this.defaults) {
      return this.defaults[name];
    }
    return false;
  }

  /**
   * Push server-resolved flags (the sensitive tier from
   * GET /feature-flags/resolved). Pass `null` on logout/401 so the keys fall
   * back to defaults-or-false — fail closed. Triggers listeners.
   */
  setServerFlags(flags: Flags | null): void {
    this.serverFlags = flags === null ? null : sanitize(flags);
    this.notify();
  }

  /**
   * Set or clear a local override. Pass `null` to clear. Triggers listeners.
   * Use this for QA toggling, test setup, or runtime DevTools tweaks.
   */
  setOverride(name: string, value: boolean | null): void {
    if (value === null) {
      delete this.overrides[name];
    } else {
      this.overrides[name] = value;
    }
    this.notify();
  }

  /** Snapshot of currently-effective flags (after applying precedence). */
  getAllFlags(): Flags {
    const merged: Record<string, boolean> = { ...this.defaults };
    if (this.remoteEverSucceeded) {
      for (const k of Object.keys(this.remote)) merged[k] = this.remote[k];
    }
    if (this.serverFlags !== null) {
      for (const k of Object.keys(this.serverFlags))
        merged[k] = this.serverFlags[k];
    }
    for (const k of Object.keys(this.overrides)) merged[k] = this.overrides[k];
    return merged;
  }

  /** Subscribe to flag changes (CDN refresh succeeded, or override set). */
  subscribe(cb: Listener): () => void {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }

  /** Stop background polling. Call on app teardown / in tests. */
  stop(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private notify(): void {
    for (const cb of this.listeners) cb();
  }
}

/** Drop non-boolean values; the schema enforces this server-side but a bad
 *  deploy or a hand-edited override file shouldn't crash callers. */
function sanitize(raw: unknown): Flags {
  if (raw === null || typeof raw !== 'object') return {};
  const out: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'boolean') out[k] = v;
  }
  return out;
}
